import { EntityType, } from '../entities/types.js';
// Re-export entity types for backwards compatibility
export { EntityType, };
// Message type identifiers
export const MessageType = {
    // Client -> Server
    CLIENT_CONNECT: 0x01,
    CLIENT_DISCONNECT: 0x02,
    CLIENT_INPUT: 0x03,
    CLIENT_PAWN_COMMAND: 0x04,
    CLIENT_CHAT: 0x05,
    CLIENT_CHUNK_REQUEST: 0x06,
    CLIENT_PING: 0x07,
    CLIENT_CAMERA_UPDATE: 0x08,
    CLIENT_CONSTRUCTION_ORDER: 0x09, // Order construction at location
    CLIENT_CANCEL_CONSTRUCTION: 0x0a, // Cancel construction site
    CLIENT_CRAFT_ORDER: 0x0b, // Queue craft order at workstation
    CLIENT_CANCEL_CRAFT: 0x0c, // Cancel craft order
    CLIENT_OPEN_WORKSTATION: 0x0d, // Request workstation data
    CLIENT_PAWN_SELECTION: 0x0e, // Notify server of selected pawns
    CLIENT_RESPAWN: 0x0f, // Request respawn after colony lost
    // Server -> Client
    SERVER_WELCOME: 0x10,
    SERVER_SNAPSHOT: 0x11,
    SERVER_ENTITY_SPAWN: 0x12,
    SERVER_ENTITY_DESPAWN: 0x13,
    SERVER_CHUNK_DATA: 0x14,
    SERVER_PLAYER_JOINED: 0x15,
    SERVER_PLAYER_LEFT: 0x16,
    SERVER_CHAT: 0x17,
    SERVER_ERROR: 0x18,
    SERVER_PONG: 0x19,
    SERVER_DELTA_SNAPSHOT: 0x1a,
    SERVER_GROUND_ITEM_SPAWN: 0x1b,
    SERVER_GROUND_ITEM_DESPAWN: 0x1c,
    SERVER_GROUND_ITEM_UPDATE: 0x1d,
    SERVER_CONSTRUCTION_SITE_SPAWN: 0x1e,
    SERVER_CONSTRUCTION_SITE_UPDATE: 0x1f,
    SERVER_CONSTRUCTION_SITE_COMPLETE: 0x20,
    SERVER_CONSTRUCTION_SITE_CANCEL: 0x21,
    SERVER_CONSTRUCTION_DELIVERY_UPDATE: 0x22, // Resource delivery status update
    // Animal Messages
    SERVER_NPC_SPAWN: 0x30,
    SERVER_NPC_DESPAWN: 0x31,
    SERVER_NPC_UPDATE: 0x32,
    SERVER_NPC_DIED: 0x33,
    // Combat Messages
    SERVER_COMBAT_EVENT: 0x40,
    SERVER_DAMAGE_EVENT: 0x41,
    SERVER_ENTITY_DIED: 0x42,
    // Inventory Messages
    SERVER_INVENTORY_UPDATE: 0x50,
    // Territory Messages
    SERVER_TERRITORY_CLAIMED: 0x60,
    SERVER_TERRITORY_LOST: 0x61,
    SERVER_TERRITORY_UPDATE: 0x62,
    // Corpse Messages
    SERVER_CORPSE_SPAWN: 0x70,
    SERVER_CORPSE_UPDATE: 0x71,
    SERVER_CORPSE_DESPAWN: 0x72,
    // Crafting Messages
    SERVER_WORKSTATION_DATA: 0x80, // Workstation state and available recipes
    SERVER_CRAFT_ORDER_QUEUED: 0x81, // Craft order added to queue
    SERVER_CRAFT_ORDER_UPDATE: 0x82, // Craft order progress update
    SERVER_CRAFT_ORDER_COMPLETE: 0x83, // Single item crafted
    SERVER_CRAFT_ORDER_CANCELLED: 0x84, // Order cancelled
    // Stockpile Messages
    SERVER_STOCKPILE_FULL: 0x90, // Full stockpile sync (on connect)
    SERVER_STOCKPILE_UPDATE: 0x91, // Incremental item update
    // Projectile Messages
    SERVER_PROJECTILE_SPAWN: 0xa0, // Projectile spawned and in flight
    SERVER_PROJECTILE_DESPAWN: 0xa1, // Projectile removed (hit or expired)
    SERVER_PROJECTILE_HIT: 0xa2, // Projectile hit target (includes damage info)
    // Time Messages
    SERVER_TIME_UPDATE: 0xb0, // Game time update (day/night cycle)
    // Quest Messages
    SERVER_QUESTS_FULL: 0xb1, // Full quest state sync (on connect)
    SERVER_QUEST_UPDATE: 0xb2, // Single quest progress update
    SERVER_SKILL_XP_GAIN: 0xb3, // Skill XP gained by a pawn
    // World Object Messages
    SERVER_WORLD_OBJECT_REMOVE: 0xd0, // World object destroyed (harvested, etc.)
    SERVER_WORLD_OBJECT_UPDATE: 0xd1, // World object state/rotation update
    // Colony Messages
    SERVER_COLONY_LOST: 0xc0, // All pawns dead, colony lost
    SERVER_RESPAWN: 0xc1, // Respawn with new pawn
};
// Input flags (bitmask)
export const InputFlag = {
    NONE: 0,
    MOVE_UP: 1 << 0,
    MOVE_DOWN: 1 << 1,
    MOVE_LEFT: 1 << 2,
    MOVE_RIGHT: 1 << 3,
    PRIMARY_ACTION: 1 << 4,
    SECONDARY_ACTION: 1 << 5,
    MODIFIER: 1 << 6,
};
// Pawn command types
export const PawnCommandType = {
    MOVE_TO: 0x01,
    ATTACK: 0x02,
    GATHER: 0x03,
    BUILD: 0x04,
    HAUL: 0x05,
    CANCEL: 0x06,
    SET_COMBAT_MODE: 0x07, // Toggle combat mode
    PICKUP_GROUND_ITEM: 0x08, // Pick up ground item
    EAT: 0x09, // Eat food from inventory
    HAUL_CORPSE: 0x0a, // Pick up and carry a corpse
    RESCUE: 0x0b, // Rescue/carry a downed pawn to bed (auto-find bed)
    PICKUP_DOWNED_PAWN: 0x0c, // Just pickup downed pawn (manual placement)
    PLACE_IN_BED: 0x0d, // Place carried pawn in specific bed
    DROP_CARRIED_PAWN: 0x0e, // Put down carried pawn on ground
    WORK_AT_WORKSTATION: 0x0f, // Assign pawn to work at workstation until queue empty
    EQUIP_ITEM: 0x10, // Equip weapon/armor from inventory
    PICKUP_AND_EAT: 0x11, // Walk to ground food, pick up and eat immediately
    HAUL_TO_BLUEPRINT: 0x12, // Haul resources to a construction site
    FISH: 0x13, // Fish at water edge or fishing spot
    SLEEP_IN_BED: 0x14, // Sleep in a specific bed/sleeping pad
    REFUEL_WORKSTATION: 0x15, // Haul fuel and refuel a fuel-powered workstation
};
// Task types for pawn actions
export const TaskType = {
    NONE: 0,
    MOVE: 1,
    HARVEST: 2, // Chop trees, mine rocks, gather bushes
    ATTACK: 3,
    BUILD: 4, // Building/constructing structures
    HAUL: 5,
    REST: 6,
    CONSTRUCT: 7, // Working on a construction site
    PICKUP_GROUND_ITEM: 8, // Pick up ground item
    EAT: 9, // Eat food from inventory
    SLEEP: 10, // Sleeping in bed or on ground
    SOCIALIZE: 11, // Social interaction with another pawn
    CRAFT: 12, // Crafting at a workstation
    RESCUE_PAWN: 13, // Pick up downed pawn and carry to bed (auto)
    PICKUP_DOWNED_PAWN: 14, // Just pickup downed pawn (manual placement)
    PLACE_IN_BED: 15, // Place carried pawn in specific bed
    DROP_PAWN: 16, // Walk to location and put down carried pawn
    PICKUP_AND_EAT: 17, // Walk to ground food and consume it
    HAUL_TO_BLUEPRINT: 18, // Haul resources to a construction site
    FISH: 19, // Fishing at water edge or fishing spot
    REFUEL_WORKSTATION: 20, // Refuel a fuel-powered workstation
};
// Pawn states
export const PawnState = {
    IDLE: 0,
    WALKING: 1,
    WORKING: 2,
    FIGHTING: 3,
    FLEEING: 4,
    SLEEPING: 5,
    CRAWLING: 6, // Downed but can still move slowly
    UNCONSCIOUS: 7, // Cannot move, must be carried
    BEING_CARRIED: 8, // Being carried by another pawn
    CARRYING: 9, // Carrying another pawn
    RESTING: 10, // In bed, healing
};
// Pawn AI behavior states (for autonomous AI system)
export const PawnAIBehavior = {
    NONE: 0,
    IDLE: 1,
    WANDERING: 2,
    SEEKING_FOOD: 3,
    EATING: 4,
    SEEKING_BED: 5,
    SLEEPING: 6,
    SOCIALIZING: 7,
};
//# sourceMappingURL=messages.js.map